import { CreditCard } from './__autogenerated__/types/CreditCard';
import { CreditCardData } from './CreditCardData';

export interface WalletTemplate {
  id: string;
  name: string;
  editorsNote: string;
  longEditorsNote: string;
  cards: CreditCard[];
  category: 'travel' | 'cashback' | 'business' | 'premium' | 'starter';
  complexity: 1 | 2 | 3 | 4 | 5;
  annualFees: string;
  creditRequired: string;
}

/**
 * Calculates the total annual fees for a wallet template based on its cards
 */
function calculateAnnualFees(cards: CreditCard[]): string {
  const totalFee = cards.reduce((sum, cardId) => {
    const cardData = CreditCardData[cardId];
    if (!cardData) {
      return sum;
    }
    const annualFee =
      cardData.fees?.annual_fee_amount_dollars ?? cardData.annualFee ?? 0;
    return sum + annualFee;
  }, 0);

  return totalFee === 0 ? '$0' : `$${totalFee.toLocaleString()}`;
}

const walletTemplatesData: Omit<WalletTemplate, 'annualFees'>[] = [
  {
    id: 'chase-ultimate-rewards-trifecta',
    name: 'Chase "Ultimate Rewards" Trifecta',
    editorsNote: '',
    longEditorsNote: '',
    cards: [
      CreditCard.CHASE_SAPPHIRE_PREFERRED_CREDIT_CARD,
      CreditCard.CHASE_FREEDOM_FLEX_CREDIT_CARD,
      CreditCard.CHASE_FREEDOM_UNLIMITED_CREDIT_CARD,
    ],
    category: 'travel',
    complexity: 3,
    creditRequired: 'Good–Excellent',
  },
  {
    id: 'amex-membership-rewards-trifecta',
    name: 'American Express Membership Rewards Trifecta',
    editorsNote: '',
    longEditorsNote: '',
    cards: [
      CreditCard.AMERICAN_EXPRESS_THE_PLATINUM_CARD,
      CreditCard.AMERICAN_EXPRESS_GOLD_CARD,
      CreditCard.BLUE_BUSINESS_PLUS_CREDIT_CARD,
    ],
    category: 'premium',
    complexity: 4,
    creditRequired: 'Excellent',
  },
  {
    id: 'citi-thankyou-points-trifecta',
    name: 'Citi "ThankYou" Points Trifecta',
    editorsNote: '',
    longEditorsNote: '',
    cards: [
      CreditCard.CITI_STRATA_PREMIER_CARD,
      CreditCard.CITI_DOUBLE_CASH_CARD,
      CreditCard.CITI_CUSTOM_CASH_CARD,
    ],
    category: 'travel',
    complexity: 3,
    creditRequired: 'Good–Excellent',
  },
  {
    id: 'capital-one-travel-dining-duo',
    name: 'Capital One "Travel & Dining" Duo',
    editorsNote: '',
    longEditorsNote: '',
    cards: [
      CreditCard.VENTURE_X_REWARDS,
      CreditCard.SAVOR_REWARDS_FOR_GOOD_CREDIT,
    ],
    category: 'travel',
    complexity: 2,
    creditRequired: 'Excellent',
  },
  {
    id: 'bank-of-america-preferred-rewards-trio',
    name: 'Bank of America "Preferred Rewards" Trio',
    editorsNote: '',
    longEditorsNote: '',
    cards: [
      CreditCard.BANK_OF_AMERICA_PREMIUM_REWARDS,
      CreditCard.BANK_OF_AMERICA_CUSTOMIZED_CASH_REWARDS,
      CreditCard.BANK_OF_AMERICA_UNLIMITED_CASH_REWARDS,
    ],
    category: 'cashback',
    complexity: 2,
    creditRequired: 'Good–Excellent',
  },
  {
    id: 'wells-fargo-simple-cashback-duo',
    name: 'Wells Fargo "Simple Cashback" Duo',
    editorsNote: '',
    longEditorsNote: '',
    cards: [CreditCard.ACTIVE_CASH_CARD, CreditCard.ATTUNE_CARD],
    category: 'cashback',
    complexity: 1,
    creditRequired: 'Good',
  },
  {
    id: '5-percent-category-maximizer-combo',
    name: '"5% Category Maximizer" Combo',
    editorsNote: '',
    longEditorsNote: '',
    cards: [
      CreditCard.CHASE_FREEDOM_FLEX_CREDIT_CARD,
      CreditCard.DISCOVER_IT_CASH_BACK_CREDIT_CARD,
      CreditCard.CITI_DOUBLE_CASH_CARD,
    ],
    category: 'cashback',
    complexity: 5,
    creditRequired: 'Good',
  },
  {
    id: 'starter-pack-cashback-duo',
    name: '"Starter Pack" Cashback Duo',
    editorsNote: '',
    longEditorsNote: '',
    cards: [
      CreditCard.DISCOVER_IT_CASH_BACK_CREDIT_CARD,
      CreditCard.QUICKSILVERONE_REWARDS,
    ],
    category: 'starter',
    complexity: 2,
    creditRequired: 'Fair/Average',
  },
  {
    id: 'chase-ink-business-trifecta',
    name: 'Chase "Ink" Business Trifecta',
    editorsNote: '',
    longEditorsNote: '',
    cards: [
      CreditCard.INK_BUSINESS_PREFERRED_CREDIT_CARD,
      CreditCard.INK_BUSINESS_CASH_CREDIT_CARD,
      CreditCard.INK_BUSINESS_UNLIMITED_CREDIT_CARD,
    ],
    category: 'business',
    complexity: 3,
    creditRequired: 'Good–Excellent',
  },
  {
    id: 'amex-business-mr-trifecta',
    name: 'Amex Business "MR" Trifecta',
    editorsNote: '',
    longEditorsNote: '',
    cards: [
      CreditCard.BUSINESS_PLATINUM_CARD,
      CreditCard.BUSINESS_GOLD_CARD,
      CreditCard.BLUE_BUSINESS_PLUS_CREDIT_CARD,
    ],
    category: 'business',
    complexity: 4,
    creditRequired: 'Excellent',
  },
];

// Calculate annual fees programmatically for each wallet template
export const walletTemplates: WalletTemplate[] = walletTemplatesData.map(
  (template) => ({
    ...template,
    annualFees: calculateAnnualFees(template.cards),
  })
);

export const categoryColors = {
  travel: '#0E80D5',
  cashback: '#28a745',
  business: '#ff9800',
  premium: '#9c27b0',
  starter: '#6c757d',
};
